import React from 'react';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';

/**
 * @typedef {Object} Section
 * @property {number} id - Unique section identifier
 * @property {string} section_type - Type of section (e.g., 'hero_carousel', 'live_draw')
 * @property {number} section_order - Display order
 * @property {boolean} is_visible - Whether the section is visible
 * @property {Object} config - Section-specific configuration
 */

/**
 * @typedef {Object} SectionRendererProps
 * @property {Section} section - The section object to render
 * @property {Object.<string, React.ComponentType>} components - Map of section_type to React component
 * @property {boolean} editMode - Whether edit mode is enabled
 * @property {Function} onTranslationUpdate - Callback for translation updates
 * @property {Section[]} sections - All sections (for accessing config of related sections)
 * @property {Object} [extraProps] - Additional props to pass to the rendered component
 */

/**
 * Reusable component for rendering page sections based on section type.
 * Handles visibility checks and component mapping automatically.
 *
 * @param {SectionRendererProps} props
 * @returns {React.ReactElement|null}
 *
 * @example
 * const components = {
 *   'recent_winners': RecentWinnersComponent,
 *   'leaderboard': LeaderboardComponent,
 *   'hero_carousel': HeroCarouselComponent,
 * };
 *
 * // Render a single section
 * <SectionRenderer
 *   section={section}
 *   components={components}
 *   editMode={editMode}
 *   onTranslationUpdate={handleTranslationUpdate}
 *   sections={allSections}
 * />
 *
 * // Render all visible sections in order
 * {sortedSections.map(section => (
 *   <SectionRenderer
 *     key={section.id || section.section_type}
 *     section={section}
 *     components={components}
 *     editMode={editMode}
 *     onTranslationUpdate={handleTranslationUpdate}
 *     sections={allSections}
 *   />
 * ))}
 */
export function SectionRenderer({
  section,
  components,
  editMode = false,
  onTranslationUpdate,
  sections = [],
  extraProps = {},
}) {
  const { isSectionVisible } = useSectionVisibility(sections);

  // Skip rendering if section is not visible
  if (!section || !isSectionVisible(section.section_type)) {
    return null;
  }

  const { section_type, config = {} } = section;

  // Get the component for this section type
  const Component = components[section_type];

  // If no component is registered for this section type, skip rendering
  if (!Component) {
    return null;
  }

  // Render the component with standard props
  return (
    <Component
      key={section.id || section_type}
      editMode={editMode}
      onTranslationUpdate={onTranslationUpdate}
      config={config}
      section={section}
      sections={sections}
      {...extraProps}
    />
  );
}

/**
 * Renders multiple sections in order.
 * Filters by visibility and sorts by section_order automatically.
 *
 * @param {Object} props
 * @param {Section[]} props.sections - Array of all sections
 * @param {Object.<string, React.ComponentType>} props.components - Map of section_type to React component
 * @param {boolean} props.editMode - Whether edit mode is enabled
 * @param {Function} props.onTranslationUpdate - Callback for translation updates
 * @param {string[]} [props.filterTypes] - Optional array of section_types to render (renders all if not provided)
 * @param {Object} [props.extraProps] - Additional props to pass to all rendered components
 * @param {string} [props.className] - Optional className for the wrapper
 * @returns {React.ReactElement}
 */
export function SectionList({
  sections = [],
  components,
  editMode = false,
  onTranslationUpdate,
  filterTypes,
  extraProps = {},
  className = '',
}) {
  const { getVisibleSections, getSortedSections } = useSectionVisibility(sections);

  // Get visible sections sorted by order
  const visibleSections = getSortedSections().filter((s) => s.is_visible);

  // Optionally filter by specific section types
  const filteredSections = filterTypes
    ? visibleSections.filter((s) => filterTypes.includes(s.section_type))
    : visibleSections;

  return (
    <div className={className}>
      {filteredSections.map((section) => (
        <SectionRenderer
          key={section.id || section.section_type}
          section={section}
          components={components}
          editMode={editMode}
          onTranslationUpdate={onTranslationUpdate}
          sections={sections}
          extraProps={extraProps}
        />
      ))}
    </div>
  );
}

export default SectionRenderer;
